import { Badge, message, Row, Space, Typography } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AllFilterIcon from "../../assets/icons/all-filter.svg";
import FilterIcon from "../../assets/icons/filter-grey.svg";
import FilterActiveIcon from "../../assets/icons/filter.svg";
import UnansweredFilterIcon from "../../assets/icons/unanswered-filter.svg";
import UnreadFilterIcon from "../../assets/icons/unread-filter.svg";
import { CONVERSATION_TYPES, CONVERSATIONS_FILTER_TYPES, CONVERSATIONS_TYPES, CREATOR, FAN, REQUEST_LOADING_STATES } from "../../helpers/constant";
import { IConversations } from "../../helpers/types";
import { useAppSelector } from "../../redux/hooks";
import {
  getUserConversations,
  getUserUnansweredConversations,
  getUserUnreadConversations,
  searchConversation,
  selectConversation,
  setCurrentFilter,
  setSearchValue,
  updateConversationHasNewMessages,
} from "../../redux/messages/messagesSlice";
import { selectConversationsDetails, selectFilteredConversations } from "../../redux/messages/Selectors/messageSeletors";
import type { AppDispatch, StoreState } from "../../redux/store";
import { authService } from "../../services";
import ConversationAvatarComponent from "../ConversationAvatarComponent";
import ConversationOnlineStatusComponent from "../ConversationOnlineStatusComponent";
import CountDownComponent from "../CountDownComponent";
import CustomDropDownComponent from "../CustomDropDownComponent";
import SpinnerComponent from "../SpinnerComponent/SpinnerComponent";
import SubscriberBadge from "../SubscriberBadge/SubscriberBadge";
import VerifiedUserIconComponent from "../VerifiedUserIconComponent/VerifiyedUserIconComponent";
import "./index.less";

type props = {
  type?: string;
  activeKey: string;
};
const NOT_FOUND_PATTERN = /^user[\s-]not[\s-]found$/i;

const getInterlocutorInfo = (interlocutor: { name?: string; username?: string } | undefined, notFoundLabel: string) => {
  if (!interlocutor) return { displayName: notFoundLabel, avatarName: "", username: undefined };

  const { name, username } = interlocutor;
  const nameNotFound = !name || NOT_FOUND_PATTERN.test(name.trim());

  return {
    displayName: nameNotFound ? notFoundLabel : name,
    avatarName: nameNotFound ? "" : name || username,
    username: username && !NOT_FOUND_PATTERN.test(username.trim()) ? username : undefined,
  };
};

// Helper function to detect if a message is an audio message
const isAudioMessage = (message: any): boolean => {
  // Check if the message has isAudio property set to true
  return message && message.isAudio === true;
};

const MessageTabListComponent: React.FC<props> = ({ activeKey, type = CONVERSATION_TYPES.GENERAL }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { loading, error, isSearching, searchValue } = useSelector((state: StoreState) => state.messages);
  const { currentFilter: filter, type: conversationType } = useSelector((state: StoreState) => state.messages);

  const conversationsData = useAppSelector((state) => selectFilteredConversations(state, activeKey, filter));
  const { page, hasNextPage } = useAppSelector((state) => selectConversationsDetails(state, activeKey, filter));
  const currentUserId = authService.getId();
  // const shouldFetch = useRef(true);

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const filterOptions = [
    { label: t("all-s"), value: CONVERSATIONS_FILTER_TYPES.ALL, icon: AllFilterIcon },
    { label: t("unread-message"), value: CONVERSATIONS_FILTER_TYPES.UNREAD, icon: UnreadFilterIcon },
    { label: t("unanswered-message"), value: CONVERSATIONS_FILTER_TYPES.UNANSWERED, icon: UnansweredFilterIcon },
  ];
  const name = authService.getUser()?.firstName;

  useEffect(() => {
    if (loading === REQUEST_LOADING_STATES.FAILED && error) {
      message.error("Something went wrong.");
    }
  }, [loading, error]);

  const loadMoreConversations = () => {
    if (hasNextPage && page) {
      const nextPage = page + 1;
      if (conversationType === CONVERSATIONS_TYPES.SEARCH) {
        dispatch(
          searchConversation({
            search: searchValue,
            page: nextPage,
            type: activeKey!,
          })
        );
      } else if (filter === CONVERSATIONS_FILTER_TYPES.UNREAD) {
        dispatch(getUserUnreadConversations({ page: nextPage, type: activeKey }));
      } else if (filter === CONVERSATIONS_FILTER_TYPES.UNANSWERED) {
        dispatch(getUserUnansweredConversations({ page: nextPage, type: activeKey }));
      } else {
        dispatch(getUserConversations({ page: nextPage, type: activeKey }));
      }
    }
  };

  const handleClick = async (conversation: IConversations) => {
    dispatch(selectConversation(conversation));
    // if (authService.getRole() === FAN && conversation.interlocutor?.userId) {
    //   await userService.getCreatorChatTypeInfos(conversation.interlocutor?.userId).then((res) => {
    //     dispatch(setCreatorChatType(res.chat));
    //   });
    // }
    dispatch(updateConversationHasNewMessages({ conversationId: conversation._id, conversationType: activeKey }));
    // if (conversation._id !== selectedConversation?._id) {
    //   //dispatch(clearSelectedConversation());
    // }
    if (authService.getRole() === CREATOR) {
      navigate(`/creator/conversation/${conversation._id}`);
    } else {
      navigate(`/fan/requestMedia/${conversation._id}`, {
        state: { activeKey },
      });
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading: loading === REQUEST_LOADING_STATES.PENDING,
    hasNextPage,
    onLoadMore: loadMoreConversations,
  });

  // TODO: Implement actual filtering logic if needed, e.g. refetching data based on the filter
  const handleFilterSelect = (value: string) => {
    setShowFilterDropdown(false);
    dispatch(setCurrentFilter(value));

    //NOTE: CLEAR SEARCH INPUT IF THE USER SEARCH AND THEN TRY TO FILTER
    dispatch(setSearchValue(""));

    if (value === CONVERSATIONS_FILTER_TYPES.UNREAD) {
      dispatch(getUserUnreadConversations({ page: 1, type: activeKey }));
    } else if (value === CONVERSATIONS_FILTER_TYPES.UNANSWERED) {
      dispatch(getUserUnansweredConversations({ page: 1, type: activeKey }));
    } else if (value === CONVERSATIONS_FILTER_TYPES.ALL) {
      dispatch(getUserConversations({ page: 1, type: activeKey }));
    }
  };

  return (
    <Space className="w-full gap-14" direction="vertical">
      {authService.getRole() === CREATOR && activeKey !== CONVERSATION_TYPES.TO_DELIVER && (
        <div className="filter-wrapper">
          <div
            ref={filterButtonRef}
            className={`filter-container ${filter !== CONVERSATIONS_FILTER_TYPES.ALL ? "filter-active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowFilterDropdown(!showFilterDropdown);
            }}
          >
            <img
              src={
                filter === CONVERSATIONS_FILTER_TYPES.UNREAD
                  ? FilterActiveIcon
                  : filter === CONVERSATIONS_FILTER_TYPES.UNANSWERED
                    ? FilterActiveIcon
                    : FilterIcon
              }
              className="filter-icon"
              alt="filter"
            />
            <Typography.Text className="filter-text">
              {filter === CONVERSATIONS_FILTER_TYPES.ALL
                ? t("filter")
                : filter === CONVERSATIONS_FILTER_TYPES.UNREAD
                  ? t("unread-message")
                  : t("unanswered-message")}
            </Typography.Text>
          </div>

          {showFilterDropdown && (
            <CustomDropDownComponent
              isFilter={true}
              options={filterOptions}
              selectedValue={filter}
              onSelect={handleFilterSelect}
              triggerElement={filterButtonRef}
              onClose={() => setShowFilterDropdown(false)}
            />
          )}
        </div>
      )}
      {activeKey === CONVERSATION_TYPES.TO_DELIVER && <div></div>}
      {loading === REQUEST_LOADING_STATES.SUCCEEDED && conversationsData?.length === 0 ? (
        <Typography.Text className="not-conversation-text">{t("you-dont-have-conversations")}</Typography.Text>
      ) : isSearching ? (
        <SpinnerComponent />
      ) : (
        conversationsData?.map((conversation: IConversations) => {
          const { displayName, avatarName, username: interlocutorUsername } = getInterlocutorInfo(conversation.interlocutor, t("user-not-found"));
          return (
            <Row key={conversation._id} className="conversation-row" onClick={() => handleClick(conversation)}>
              <div className={`conversation-details ${activeKey === CONVERSATION_TYPES.TO_DELIVER ? "to-deliver" : ""}`}>
                <div className="avatar-container">
                  <ConversationAvatarComponent username={interlocutorUsername} avatar={conversation.interlocutor?.avatar} name={avatarName} />
                </div>
                <div className="conversation-info-container">
                  <div className="conversation-info-user">
                    <Typography.Text className={`messageNameLabel ${conversation.hasNewMessages ? "text-bold-white-color" : ""}`}>
                      {displayName}
                    </Typography.Text>
                    <VerifiedUserIconComponent
                      userId={conversation.interlocutor?.userId}
                      verified={conversation.interlocutor?.isVerified}
                      fetch={false}
                    />
                    {(() => {
                      const isSubscribed = conversation.isFanSubscribed && new Date(conversation.isFanSubscribed) > new Date();
                      return authService.getRole() === CREATOR && isSubscribed && <SubscriberBadge />;
                    })()}
                  </div>
                  <div className="conversation-info-status">
                    <Typography.Text className={`messageStatusLabel ${conversation.hasNewMessages ? "text-bold-white-color" : ""}`}>
                      {conversation.lastMessage ? (
                        <span className="w-full">
                          {/* If text exists, show it. Otherwise check if it's audio or other media */}
                          {conversation.lastMessage.text
                            ? conversation.lastMessage.text.replace(
                                /\/name/g,
                                authService.getRole() === FAN ? name : conversation.interlocutor?.firstName
                              )
                            : isAudioMessage(conversation.lastMessage)
                              ? t(conversation.lastMessage.sentBy === currentUserId ? "audio-sent" : "audio-received")
                              : t(conversation.lastMessage.sentBy === currentUserId ? "media_sent" : "media_received")}
                        </span>
                      ) : (
                        <ConversationOnlineStatusComponent username={conversation.interlocutor?.username} className={"messageStatusLabel"} />
                      )}
                    </Typography.Text>
                  </div>
                </div>
              </div>
              <div className="conversation-clock-badge">
                {activeKey === CONVERSATION_TYPES.TO_DELIVER ? (
                  <div className="clock-background">
                    <CountDownComponent targetDate={conversation?.undeliveredMessage?.updatedAt!} showSeconds={false} />
                  </div>
                ) : null}
                {conversation.hasNewMessages && <Badge className="messageNewBadge" status="success" />}
              </div>
            </Row>
          );
        })
      )}
      {(loading === REQUEST_LOADING_STATES.PENDING || hasNextPage) && !isSearching && <SpinnerComponent refProp={sentryRef} />}
    </Space>
  );
};

export default MessageTabListComponent;
